// Price Lists data layer (Phase 4, P4-WP11b) — named price lists + their per-product /
// per-unit / per-min-qty price rows. The SINGLE interface between the app and
// public.price_lists / public.price_list_items
// (supabase/migrations/20260721130000_price_lists.sql).
//
// MODEL: a company has 0+ named price lists; at most one is the default (the resolver's
// company-wide fallback, WP11c). Each list holds rows keyed by (product, unit-or-base,
// min_qty); min_qty is quantity-break-ready but nothing reads it yet. Prices are
// numeric(14,2) — the same precision as products.wholesale_price.
//
// company_id is owned + frozen by DB triggers (parent from auth, rows from the parent
// list); we never send it. The DB enforces uniqueness / checks / RLS; this layer keeps
// the "at most one default" invariant via the same unset-old-then-set-new swap WP11a
// used for base/default-sell units. Reads/writes price_lists + price_list_items only
// (plus a read-only product_units existence check to validate unit ownership).

import { supabase } from "./supabaseClient";

const PL = "price_lists";
const PLI = "price_list_items";
const SELECT = "id, name, currency, is_default, status, notes, created_at, updated_at";
const SELECT_ITEM = "id, price_list_id, product_id, unit_id, min_qty, unit_price";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

function toError(error, fallback, dup) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = dup || "That already exists (duplicate)."; break;
    case "23514": message = "A value is out of range (min quantity must be ≥ 1, price ≥ 0)."; break;
    case "23503": message = "That unit does not belong to this product."; break;   // composite FK (WP11c)
    case "23502": message = "A required field is missing."; break;
    case "42501": message = "You do not have access to this price list."; break;
    case "PGRST116": message = "Price list not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// Unit-ownership (a price row's unit must belong to its product) is enforced by the DB
// composite FK (product_id, unit_id) → product_units(product_id, id) since WP11c; its
// 23503 maps to the friendly message above. No data-layer pre-validation round-trip.

// ── Price lists ───────────────────────────────────────────────────────────────

/** All price lists for the company, default first then by name (with item_count). */
export async function getPriceLists() {
  const { data, error } = await client().from(PL).select(`${SELECT}, price_list_items ( count )`)
    .order("is_default", { ascending: false }).order("name", { ascending: true });
  if (error) throw toError(error, "Could not load price lists");
  return (data || []).map(({ price_list_items, ...pl }) => ({ ...pl, item_count: price_list_items?.[0]?.count ?? 0 }));
}

/** One price list (or null if not found / not visible). */
export async function getPriceList(id) {
  const { data, error } = await client().from(PL).select(SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load price list");
  return data || null;
}

/** One price list with its item rows embedded (ordered by product then min_qty). */
export async function getPriceListWithItems(id) {
  const { data, error } = await client().from(PL)
    .select(`${SELECT}, price_list_items ( ${SELECT_ITEM} )`).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load price list");
  if (!data) return null;
  const items = (data.price_list_items || []).slice()
    .sort((a, b) => (a.product_id < b.product_id ? -1 : a.product_id > b.product_id ? 1 : 0) || (a.min_qty - b.min_qty));
  return { ...data, items };
}

export async function createPriceList(draft = {}) {
  const c = client();
  if (!draft.name || !String(draft.name).trim()) throw new Error("Price list name is required.");
  if (draft.is_default) {   // swap: clear the current default before inserting the new one
    const { error } = await c.from(PL).update({ is_default: false }).eq("is_default", true);
    if (error) throw toError(error, "Could not update the default price list");
  }
  const row = {
    name: String(draft.name).trim(),
    currency: draft.currency || "EUR",
    is_default: !!draft.is_default,
    status: draft.status || "active",
    notes: draft.notes || null,
  };
  const { data, error } = await c.from(PL).insert(row).select(SELECT).single();
  if (error) throw toError(error, "Could not create price list", "A price list with that name already exists.");
  return data;
}

/** Update the editable fields. is_default is managed via setDefaultPriceList (the swap). */
export async function updatePriceList(id, patch = {}) {
  const upd = {};
  if (patch.name !== undefined) upd.name = String(patch.name).trim();
  if (patch.currency !== undefined) upd.currency = patch.currency || "EUR";
  if (patch.status !== undefined) upd.status = patch.status;
  if (patch.notes !== undefined) upd.notes = patch.notes || null;
  const { data, error } = await client().from(PL).update(upd).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not update price list", "A price list with that name already exists.");
  return data;
}

/** Soft-retire a list (never hard-delete); also clears its default flag. */
export async function archivePriceList(id) {
  const { data, error } = await client().from(PL).update({ status: "archived", is_default: false }).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not archive price list");
  return data;
}

/**
 * Make this list the company default, enforcing "at most one default" with the same
 * unset-old-then-set-new swap WP11a used for base units — so the partial-unique index
 * can never reject a valid switch. RLS scopes both writes to the caller's company.
 */
export async function setDefaultPriceList(id) {
  const c = client();
  const { error: e1 } = await c.from(PL).update({ is_default: false }).eq("is_default", true).neq("id", id);
  if (e1) throw toError(e1, "Could not update the default price list");
  const { data, error } = await c.from(PL).update({ is_default: true }).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not set the default price list");
  return data;
}

// ── Price list items ──────────────────────────────────────────────────────────

/** All rows in a list, ordered by product then min_qty. */
export async function listItems(priceListId) {
  const { data, error } = await client().from(PLI).select(SELECT_ITEM)
    .eq("price_list_id", priceListId)
    .order("product_id", { ascending: true }).order("min_qty", { ascending: true });
  if (error) throw toError(error, "Could not load price list items");
  return data || [];
}

export async function addItem(priceListId, { product_id, unit_id = null, min_qty = 1, unit_price } = {}) {
  const c = client();
  if (!product_id) throw new Error("A product is required.");
  if (unit_price == null || unit_price === "") throw new Error("A unit price is required.");
  const row = { price_list_id: priceListId, product_id, unit_id: unit_id || null, min_qty: Number(min_qty) || 1, unit_price: Number(unit_price) };
  const { data, error } = await c.from(PLI).insert(row).select(SELECT_ITEM).single();
  if (error) throw toError(error, "Could not add the price", "This product already has a price for that unit and quantity in this list.");
  return data;
}

export async function updateItem(id, patch = {}) {
  const c = client();
  const upd = {};
  if (patch.product_id !== undefined) upd.product_id = patch.product_id;
  if (patch.unit_id !== undefined) upd.unit_id = patch.unit_id || null;
  if (patch.min_qty !== undefined) upd.min_qty = Number(patch.min_qty) || 1;
  if (patch.unit_price !== undefined) upd.unit_price = Number(patch.unit_price);
  const { data, error } = await c.from(PLI).update(upd).eq("id", id).select(SELECT_ITEM).single();
  if (error) throw toError(error, "Could not update the price", "This product already has a price for that unit and quantity in this list.");
  return data;
}

export async function removeItem(id) {
  const { error } = await client().from(PLI).delete().eq("id", id);
  if (error) throw toError(error, "Could not remove the price");
}

/** Every price row for a product across the company's lists (for the catalogue view). */
export async function getPriceListItemsForProduct(productId) {
  const { data, error } = await client().from(PLI)
    .select("id, price_list_id, unit_id, min_qty, unit_price, price_lists ( id, name, currency, is_default )")
    .eq("product_id", productId)
    .order("min_qty", { ascending: true });
  if (error) throw toError(error, "Could not load prices for this product");
  return data || [];
}
